/**
 * Feld-Auswahl je (Channel, Texttyp): welche Texttypen sind angehakt + welche
 * Vorlage je Texttyp (Spec 2026-07-24 §2/§4) — Kanal-Akkordeon in Schritt 2.
 */

import { useState } from 'react'

import { ALL_ATTRIBUTE_CODES } from './attributeDefaults'
import type { BulkAttributeCode } from '@/lib/api/channel-content-bulk'

export interface FieldSelection {
  enabled: boolean
  templateId: string | null
  /** GOKI-Standard-Tool (TOOL_DEFINITIONS) statt eigener Vorlage — exklusiv zu templateId. */
  systemToolCode: string | null
  rememberDefault: boolean
}

export interface RecipeDefault {
  templateId: string | null
  systemToolCode: string | null
}

const EMPTY_FIELD: FieldSelection = {
  enabled: false,
  templateId: null,
  systemToolCode: null,
  rememberDefault: false,
}

export type FieldSelections = Record<string, Record<BulkAttributeCode, FieldSelection>>

export function useFieldSelections() {
  const [fieldSelections, setFieldSelections] = useState<FieldSelections>({})

  const setFieldSelection = (
    channelId: string,
    attributeCode: BulkAttributeCode,
    patch: Partial<FieldSelection>,
  ) =>
    setFieldSelections((prev) => ({
      ...prev,
      [channelId]: {
        ...prev[channelId],
        [attributeCode]: { ...EMPTY_FIELD, ...prev[channelId]?.[attributeCode], ...patch },
      },
    }))

  const initChannelDefaults = (
    channelId: string,
    recipeDefaults?: Partial<Record<BulkAttributeCode, RecipeDefault>>,
  ) =>
    setFieldSelections((prev) => {
      if (prev[channelId]) return prev
      const defaults = Object.fromEntries(
        ALL_ATTRIBUTE_CODES.map((code) => [
          code,
          {
            ...EMPTY_FIELD,
            enabled: true,
            templateId: recipeDefaults?.[code]?.templateId ?? null,
            systemToolCode: recipeDefaults?.[code]?.systemToolCode ?? null,
          },
        ]),
      ) as Record<BulkAttributeCode, FieldSelection>
      return { ...prev, [channelId]: defaults }
    })

  return { fieldSelections, setFieldSelection, initChannelDefaults }
}
